import { useState, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { FlightSortOptions } from 'constants/flight'
import useFlightSortOptions from 'hooks/Flights/useFlightSortOptions'
import { getOtherSortOptions } from 'lib/flights/flightUtils'
import FlightDropdownOption from './FlightDropdownToggle/FlightDropdownOption'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import noop from 'lib/function/noop'
import Modal from 'LuxKit/Modal/Modal'
import type { FlightDropdownOption as SelectOption } from 'components/Flights/types'
import styled from 'styled-components'
import { rem } from 'polished'

interface Props {
  compactHeader?: boolean
  sort?: FlightSortOptions
  destinationAirportCode: string
  originAirportCode: string
  onChange?: (newSort: FlightSortOptions) => void
}

const SortChip = styled(AssistChip)`
  &.compact-header-chip {
    width: 100%;
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    justify-content: center;
    padding-inline: ${rem(8)};
    --chip-stroke-color: ${props => props.theme.palette.neutral.surface.lightForeground};

    &:hover {
      --chip-stroke-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    }

    > div {
      font-size: ${rem(12)};
      line-height: ${rem(16)};
    }
  }
`

function FlightSortingMobile(props: Props) {
  const { compactHeader, sort, onChange = noop, originAirportCode, destinationAirportCode } = props
  const intl = useIntl()
  const sortOptions = useFlightSortOptions()
  const allOptions = useMemo(() => [
    ...sortOptions,
    ...getOtherSortOptions(originAirportCode, destinationAirportCode),
  ], [sortOptions, originAirportCode, destinationAirportCode])

  const [open, setOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<FlightSortOptions | undefined>(sort)

  const onOptionsOpen = () => {
    setOpen(true)
    setSelectedOption(sort)
  }

  const onOptionsClose = () => {
    setOpen(false)
  }

  const onOptionChange = useCallback((option: SelectOption<FlightSortOptions>) => {
    setSelectedOption(option.value)
  }, [])

  const onApplySorting = () => {
    setOpen(false)
    onChange(selectedOption)
  }

  return (
    <div>
      <SortChip
        className={compactHeader ? 'compact-header-chip' : undefined}
        size="medium"
        variant="filled"
        onClick={onOptionsOpen}
        endIcon={<DropdownChevron open={open} />}
      >
        {intl.formatMessage(
          {
            id: 'm9Lv0X4a94q8rmfDdCMJ',
            defaultMessage: 'Sort: {selectedLabel}',
            description: 'Chip showing currently selected flight sort option on mobile',
          },
          { selectedLabel: allOptions.find(option => option.value === sort)?.label },
        )}
      </SortChip>

      <Modal
        title={intl.formatMessage({
          id: 'AeDjKaQXmfUXFGtQUqGr',
          defaultMessage: 'Sort',
          description: 'Title for flight sorting modal on mobile in the flights flow',
        })}
        primaryActionText={intl.formatMessage({
          id: 'ISCgXTZtp0AwoO4nOXOe',
          defaultMessage: 'Apply',
          description: 'Button to apply selected flight sort option on mobile',
        })}
        onPrimaryActionClick={onApplySorting}
        isOpen={open}
        onClose={onOptionsClose}
      >
        <RadioGroup>
          {allOptions.map(option => (
            <FlightDropdownOption
              key={option.value}
              checked={selectedOption === option.value}
              option={option}
              onChange={onOptionChange}
              name="flights-sort"
            />
          ))}
        </RadioGroup>
      </Modal>
    </div>
  )
}

export default FlightSortingMobile
